import {FC, useCallback, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {TaxCombination} from 'utils/tax';

import Modal from '../Modal';
import DuplicateErrorMessage from './DuplicateErrorMessage';
import RadioTaxInclusivity from './RadioTaxInclusivity';
import RadioTaxRate from './RadioTaxRate';
import RadioTaxType from './RadioTaxType';
import {taxCombinationModal} from './styles.module.css';
import {DEFAULT_FORM_FIELD, ModalFormField, prepareData} from './utils';

type TaxCombinationModalProps = {
    checkDuplicate?: (newTaxCombination: TaxCombination) => boolean;
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: (newTaxCombination: TaxCombination) => void;
    useAdvancedTaxSettings?: 'unspecified' | boolean;
};

const TaxCombinationModal: FC<TaxCombinationModalProps> = ({
    checkDuplicate,
    isOpen,
    onCancel,
    onConfirm,
    useAdvancedTaxSettings = 'unspecified',
}) => {
    const form = useForm<ModalFormField>({
        defaultValues: DEFAULT_FORM_FIELD,
        mode: 'onChange',
        shouldFocusError: false,
    });

    // Set Initial Modal Value
    useEffect(() => {
        if (isOpen) {
            form.reset(DEFAULT_FORM_FIELD);
        }
    }, [form, isOpen]);

    const onModalSubmit = useCallback(
        (formData: ModalFormField) => {
            const newTaxCombination = prepareData(formData);
            onConfirm(newTaxCombination);
        },
        [onConfirm],
    );

    return (
        <Modal isOpen={isOpen} onClose={onCancel} preventOutsideClick={true}>
            <FormProvider {...form}>
                <Modal.Header>{t`Custom Tax Setting`}</Modal.Header>
                <Modal.Body
                    className={twMerge(
                        taxCombinationModal,
                        'p-4 pb-0 flex flex-col gap-2 min-h-[250px]',
                    )}
                >
                    <div className="grid grid-cols-3 gap-2">
                        <RadioTaxType
                            useAdvancedTaxSettings={useAdvancedTaxSettings}
                        />
                        <RadioTaxRate />
                        <RadioTaxInclusivity />
                    </div>
                    <DuplicateErrorMessage checkDuplicate={checkDuplicate} />
                </Modal.Body>
                <Modal.Footer>
                    <Modal.Button
                        kind="tertiary"
                        onClick={onCancel}
                        size="sm"
                        type="cancel"
                    >
                        {t`Cancel`}
                    </Modal.Button>
                    <Modal.Button
                        disabled={!!form.formState.errors?.checkDuplicate}
                        onClick={form.handleSubmit(onModalSubmit)}
                        size="sm"
                        type="submit"
                    >
                        {t`OK`}
                    </Modal.Button>
                </Modal.Footer>
            </FormProvider>
        </Modal>
    );
};

export default TaxCombinationModal;
